import { createFileRoute } from "@tanstack/react-router";
import { Plus, XIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useUserSitesWithCTEIds } from "@/api/auth/sites";
import { useListEquipment } from "@/api/client-equipment";
import {
  useListInspectionsInfinite,
  useListScopeOfWorks,
} from "@/api/client-inspection";
import { PageStatus } from "@/components/page-status";
import { SelectedSiteHero } from "@/components/selected-site-hero";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CreateInspectionSheet } from "@/sections/inspections/create-inspection-sheet";
import {
  CATEGORY_LABELS,
  FILTER_LABELS,
  InspectionFilterPopover,
} from "@/sections/inspections/inspection-filter-popover";
import { InspectionStatusGuide } from "@/sections/inspections/status-guide-sheet";
import { useAuth } from "@/hooks/use-auth";
import { useInspectionFilterStore } from "@/stores/inspection-filter-store";
import { useSiteStore } from "@/stores/site-store";
import { InspectionsTable } from "../../../sections/inspections/table";

const PILL_VARIANTS = {
  types: "secondary",
  compliances: "outline",
} as const;

type InspectionTab =
  | "all"
  | "upcoming"
  | "scheduled"
  | "in_progress"
  | "completed"
  | "planning";

// Status presets applied when a tab (other than "all") is active.
const TAB_STATUSES: Record<Exclude<InspectionTab, "all">, string[]> = {
  upcoming: ["UPCOMING"],
  scheduled: ["SCHEDULED"],
  in_progress: ["IN_PROGRESS"],
  completed: ["COMPLETED"],
  planning: ["PLANNING"],
};

function InspectionsPage() {
  const { isPlatformAdmin, isPending } = useAuth();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Default tab depends on role, but role isn't known synchronously on first
  // paint — useAuth() hydrates from the session store after mount. Seeding the
  // tab from isPlatformAdmin in a useState initializer would pin admins to the
  // empty "Scheduled" tab (CTE-112), so start undefined and resolve the default
  // reactively below once auth settles.
  const [activeTab, setActiveTab] = useState<InspectionTab | undefined>(
    undefined,
  );
  const [createOpen, setCreateOpen] = useState(false);

  // Apply the role-based default tab once auth has hydrated. Admins land on
  // "all" (they oversee every status); everyone else lands on "scheduled",
  // their working queue. Runs only while no tab has been chosen, so it never
  // clobbers an explicit user selection or re-fires on role churn.
  useEffect(() => {
    if (isPending || activeTab !== undefined) return;
    setActiveTab(isPlatformAdmin ? "all" : "scheduled");
  }, [isPending, isPlatformAdmin, activeTab]);

  // Fetch user's sites enriched with CTE internal IDs
  const { data: sites, isLoading: sitesLoading } = useUserSitesWithCTEIds();

  // Global site selection store (initialized by sidebar workspace selector)
  const { selectedSiteId } = useSiteStore();

  // Persisted filter and sort store
  const { types, compliances, sortBy, sortOrder, removeFilter, clearAll } =
    useInspectionFilterStore();

  const activeFilterCount = types.length + compliances.length;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  // Tabs are the sole source of status filtering; "all" clears it. While the
  // default tab is still resolving (undefined), don't constrain by status —
  // this prevents a flash of the empty "Scheduled" tab before auth hydrates.
  const effectiveStatuses = useMemo(
    () =>
      activeTab === undefined || activeTab === "all"
        ? []
        : TAB_STATUSES[activeTab],
    [activeTab],
  );

  // Memoize params to prevent unnecessary query key changes
  const queryParams = useMemo(
    () => ({
      limit: 50,
      siteId: selectedSiteId ?? "",
      status:
        effectiveStatuses.length > 0 ? effectiveStatuses.join(",") : undefined,
      inspectionType: types.length > 0 ? types.join(",") : undefined,
      compliance: compliances.length > 0 ? compliances.join(",") : undefined,
      sortBy: sortBy ?? undefined,
      sortOrder: sortOrder ?? undefined,
    }),
    [selectedSiteId, effectiveStatuses, types, compliances, sortBy, sortOrder],
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    isError,
  } = useListInspectionsInfinite(queryParams, {
    query: {
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage?.data || lastPage.data.length < 50) return undefined;
        return allPages.length * 50;
      },
      placeholderData: (previousData) => previousData,
      enabled: !!selectedSiteId,
    },
  });

  const flatData = useMemo(() => {
    const all =
      data?.pages
        .flatMap((page) => page?.data ?? [])
        .filter((item) => item !== null) ?? [];
    // Deduplicate by ID — placeholderData can cause stale pages to overlap
    // with fresh pages during filter/pagination transitions
    const seen = new Set<string>();
    return all.filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }, [data?.pages]);

  // Server-side total from the first page response
  const total = data?.pages[0]?.total ?? 0;

  // Resolve equipmentId → tag and scopeOfWorkId → name for the table.
  const { data: equipmentList } = useListEquipment(
    { siteId: selectedSiteId ?? "", limit: 1000 },
    { query: { enabled: !!selectedSiteId } },
  );
  const equipmentMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const eq of equipmentList?.data ?? []) {
      map.set(eq.id, eq.tag);
    }
    return map;
  }, [equipmentList]);

  const { data: scopeOfWorks } = useListScopeOfWorks(
    { site: selectedSiteId ?? "" },
    { query: { enabled: !!selectedSiteId } },
  );
  const sowMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const sow of scopeOfWorks ?? []) {
      map.set(sow.id, sow.name);
    }
    return map;
  }, [scopeOfWorks]);

  // Filter by search term client-side
  const filteredData = useMemo(() => {
    if (!debouncedSearch) return flatData;
    const searchLower = debouncedSearch.toLowerCase();
    return flatData.filter(
      (item) =>
        item.id?.toLowerCase().includes(searchLower) ||
        item.equipmentId?.toLowerCase().includes(searchLower) ||
        item.inspectionType?.toLowerCase().includes(searchLower) ||
        item.status?.toLowerCase().includes(searchLower),
    );
  }, [flatData, debouncedSearch]);

  if (sitesLoading) {
    return <div className="p-4 lg:p-7">Loading assets...</div>;
  }

  if (!sites || sites.length === 0) {
    return (
      <PageStatus
        variant="empty"
        title="No assets available"
        description="You're not a member of any asset yet, so there are no inspections to show. Contact your administrator to be added to an asset."
      />
    );
  }

  if (isError) {
    toast.error("Failed to load inspection data. Please try again later.");
    return null;
  }

  // Build a flat list of active filter pills for rendering.
  const activeFilters = [
    ...types.map((v) => ({ category: "types" as const, value: v })),
    ...compliances.map((v) => ({
      category: "compliances" as const,
      value: v,
    })),
  ];

  // Whether we're trimming rows client-side (search) — drives whether to
  // show the server total or the filtered count.
  const clientFiltering = debouncedSearch.length > 0;
  const displayedCount = clientFiltering ? filteredData.length : total;

  return (
    <div className="min-w-0">
      <div className="p-4 lg:p-7">
        <div className="min-w-0 flex-col gap-4">
          <SelectedSiteHero className="mb-6" />

          {/* Status tabs, with the status guide beside them */}
          <div className="px-4 lg:px-6 mb-4 flex flex-wrap items-center justify-between gap-2">
            <Tabs
              value={activeTab ?? "all"}
              onValueChange={(value) => setActiveTab(value as InspectionTab)}
            >
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
                <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
                <TabsTrigger value="in_progress">In Progress</TabsTrigger>
                <TabsTrigger value="completed">Completed</TabsTrigger>
                {isPlatformAdmin && (
                  <TabsTrigger value="planning">Planning</TabsTrigger>
                )}
              </TabsList>
            </Tabs>
            <InspectionStatusGuide />
          </div>

          {/* Header row: Search | Filter Button | Row Count */}
          <div className="flex items-center justify-between px-4 lg:px-6 shrink-0 gap-4">
            {/* Search input */}
            <div className="relative flex-1 max-w-md">
              <Input
                placeholder="Search inspections..."
                value={search ?? ""}
                onChange={(event) => setSearch(event.target.value)}
                className="w-full"
              />
              {isFetching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                </div>
              )}
            </div>

            {/* Filter button + active pills */}
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <InspectionFilterPopover />

              {activeFilterCount > 0 && (
                <>
                  <div className="relative min-w-0 flex-1 overflow-hidden">
                    <div className="flex items-center gap-1.5 overflow-hidden">
                      {activeFilters.map((filter) => (
                        <button
                          key={`${filter.category}-${filter.value}`}
                          type="button"
                          className="shrink-0"
                          onClick={() =>
                            removeFilter(filter.category, filter.value)
                          }
                          aria-label={`Remove ${CATEGORY_LABELS[filter.category]} filter ${FILTER_LABELS[filter.value] ?? filter.value}`}
                        >
                          <Badge
                            variant={PILL_VARIANTS[filter.category]}
                            className="gap-1 pr-1 cursor-pointer"
                          >
                            <span className="text-[10px] opacity-60 uppercase">
                              {CATEGORY_LABELS[filter.category]}:
                            </span>
                            {FILTER_LABELS[filter.value] ?? filter.value}
                            <XIcon className="size-3 opacity-60 hover:opacity-100" />
                          </Badge>
                        </button>
                      ))}
                    </div>
                    {/* Fade overlay when pills overflow */}
                    <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-background to-transparent" />
                  </div>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground text-xs underline underline-offset-2 cursor-pointer shrink-0"
                    onClick={clearAll}
                  >
                    Clear
                  </button>
                </>
              )}
            </div>

            {/* Row count */}
            <div className="text-muted-foreground text-sm whitespace-nowrap shrink-0">
              {isLoading
                ? "Loading..."
                : clientFiltering && displayedCount !== total
                  ? `${displayedCount} of ${total} row(s)`
                  : `${displayedCount} row(s)`}
            </div>

            {/* New inspection button — admin-only; inspectors execute
                scheduled inspections, they don't create ad-hoc ones. */}
            {isPlatformAdmin && selectedSiteId && (
              <Button
                className="gap-2 shrink-0"
                onClick={() => setCreateOpen(true)}
              >
                <Plus className="h-4 w-4" />
                New inspection
              </Button>
            )}
          </div>

          {selectedSiteId ? (
            <InspectionsTable
              data={filteredData}
              equipmentMap={equipmentMap}
              sowMap={sowMap}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
            />
          ) : (
            <PageStatus
              variant="empty"
              title="No workspace selected"
              description="Select a workspace from the sidebar to view its inspections."
            />
          )}
        </div>
      </div>
      {isPlatformAdmin && selectedSiteId && (
        <CreateInspectionSheet
          open={createOpen}
          onOpenChange={setCreateOpen}
          siteId={selectedSiteId}
        />
      )}
    </div>
  );
}

export const Route = createFileRoute("/dashboard/inspections/")({
  component: InspectionsPage,
  staticData: {
    title: "Inspections",
  },
});
